import tinycolor from 'tinycolor2';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import DepsService from 'services/dependencies';
import * as api from 'app/resources/theme.api';
import actionTypes from './action-types';

export const categories = [
  {
    id: 1,
    name: () => DepsService.gettextCatalog.getString('Colors'),
  },
  {
    id: 2,
    name: () => DepsService.gettextCatalog.getString('Background'),
  },
  {
    id: 3,
    name: () => DepsService.gettextCatalog.getString('Typography'),
  },
  {
    id: 4,
    name: () => DepsService.gettextCatalog.getString('Actions'),
  },
  {
    id: 5,
    name: () => DepsService.gettextCatalog.getString('Charts'),
  },
  {
    id: 6,
    name: () => DepsService.gettextCatalog.getString('Divider'),
  },
];

export const productTheme = [
  {
    id: '--primary-color',
    categoryId: 1,
    value: '#1971b6',
    name: 'primary',
    complementaryColors: true,
  },
  {
    id: '--secondary-color',
    categoryId: 1,
    value: '#14599a',
    name: 'secondary',
    complementaryColors: true,
  },
  {
    id: '--error-color',
    categoryId: 1,
    value: '#e02010',
    name: 'error',
    complementaryColors: false,
  },
  {
    id: '--warning-color',
    categoryId: 1,
    value: '#f39f2b',
    name: 'warning',
    complementaryColors: false,
  },
  {
    id: '--info-color',
    categoryId: 1,
    value: '#2fa2ff',
    name: 'info',
    complementaryColors: true,
  },
  {
    id: '--success-color',
    categoryId: 1,
    value: '#0b8043',
    name: 'success',
    complementaryColors: false,
  },
  {
    id: '--background-color',
    categoryId: 2,
    value: '#f0f5f8',
    name: 'background',
    complementaryColors: false,
  },
  {
    id: '--background-paper-color',
    categoryId: 2,
    value: '#ffffff',
    name: 'background paper',
    complementaryColors: false,
  },
  {
    id: '--primary-text-color',
    categoryId: 3,
    value: '#4d4d4d',
    name: 'primary text',
    complementaryColors: true,
  },
  {
    id: '--action-disabled-color',
    categoryId: 4,
    value: '#5b616b',
    name: 'disabled',
    complementaryColors: false,
  },
  {
    id: '--action-disabled-background-color',
    categoryId: 4,
    value: '#f1f1f1',
    name: 'disabled background',
    complementaryColors: false,
  },
  {
    id: '--chart-color-1',
    categoryId: 5,
    value: '#673ab7',
    name: 'color 1',
    complementaryColors: false,
  },
  {
    id: '--chart-color-2',
    categoryId: 5,
    value: '#e91e63',
    name: 'color 2',
    complementaryColors: false,
  },
  {
    id: '--chart-color-3',
    categoryId: 5,
    value: '#9c27b0',
    name: 'color 3',
    complementaryColors: false,
  },
  {
    id: '--chart-color-4',
    categoryId: 5,
    value: '#f44336',
    name: 'color 4',
    complementaryColors: false,
  },
  {
    id: '--chart-color-5',
    categoryId: 5,
    value: '#2196f3',
    name: 'color 5',
    complementaryColors: false,
  },
  {
    id: '--chart-color-6',
    categoryId: 5,
    value: '#00bcd4',
    name: 'color 6',
    complementaryColors: false,
  },
  {
    id: '--chart-color-7',
    categoryId: 5,
    value: '#2f9687',
    name: 'color 7',
    complementaryColors: false,
  },
  {
    id: '--chart-color-8',
    categoryId: 5,
    value: '#4caf50',
    name: 'color 8',
    complementaryColors: false,
  },
  {
    id: '--chart-color-9',
    categoryId: 5,
    value: '#cddc39',
    name: 'color 9',
    complementaryColors: false,
  },
  {
    id: '--chart-color-10',
    categoryId: 5,
    value: '#ffc107',
    name: 'color 10',
    complementaryColors: false,
  },
  {
    id: '--chart-color-11',
    categoryId: 5,
    value: '#ffe722',
    name: 'color 11',
    complementaryColors: false,
  },
  {
    id: '--chart-color-12',
    categoryId: 5,
    value: '#795548',
    name: 'color 12',
    complementaryColors: false,
  },
  {
    id: '--divider-color',
    categoryId: 6,
    value: '#cccccc',
    name: 'divider',
    complementaryColors: false,
  },
];

export const getChartColors = (theme) => theme.reduce((colorsArr, color) => {
  if (color.id.includes('--chart-color')) {
    return [...colorsArr, color.value];
  }

  return colorsArr;
}, []);

const setColor = ({ id, value, complementaryColors }) => {
  const root = window.document.documentElement;
  const color = new tinycolor(value).toHsl();
  if (complementaryColors) {
    const hue = Math.round(color.h);
    const saturation = Math.round(color.s * 100);
    const lightness = Math.round(color.l * 100);

    root.style.setProperty(`${id}-h`, hue);
    root.style.setProperty(`${id}-s`, saturation);
    root.style.setProperty(`${id}-l`, lightness);

    root.style.setProperty(`${id}-lighter`, `hsl(${hue}, ${saturation}%, ${lightness + 15}%)`);
    root.style.setProperty(`${id}-darker`, `hsl(${hue}, ${saturation}%, ${lightness - 10}%)`);
  }
  root.style.setProperty(id, value);
};

export const setCurrentThemeSuccess = (theme) => ({
  type: actionTypes.SET_CURRENT_THEME_SUCCESS,
  theme,
});

export const updateThemeColorSuccess = (color) => ({
  type: actionTypes.UPDATE_THEME_COLOR_SUCCESS,
  color,
});

export const setTheme = () => (dispatch, getState) => {
  const { config } = getState();
  const { colors: lotteryTheme } = config;
  const currentTheme = (!isEmpty(lotteryTheme) && JSON.parse(lotteryTheme)) || productTheme;

  currentTheme.forEach((color) => setColor(color));

  dispatch(setCurrentThemeSuccess(cloneDeep(currentTheme)));
};

export const restoreTheme = () => (dispatch) => {
  dispatch(setCurrentThemeSuccess(cloneDeep(productTheme)));

  productTheme.forEach((color) => setColor(color));
};

export const updateThemeColor = (color) => (dispatch) => {
  setColor(color);
  dispatch(updateThemeColorSuccess(color));
};

export const saveThemeRequest = () => ({
  type: actionTypes.SAVE_THEME_REQUEST,
  error: false,
});

export const saveThemeSuccess = () => ({
  type: actionTypes.SAVE_THEME_SUCCESS,
});

export const saveThemeFailure = () => ({
  type: actionTypes.SAVE_THEME_FAILURE,
  error: true,
});

export const saveTheme = () => async (dispatch, getState) => {
  try {
    const { theme } = getState();
    dispatch(saveThemeRequest());
    await api.saveTheme(theme.colors);

    dispatch(saveThemeSuccess());
  } catch (error) {
    dispatch(saveThemeFailure());
    console.error('SAVE_THEME', error);
  }
};



// WEBPACK FOOTER //
// ./src/modules/app/actions/theme.actions.js